import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { IoDocumentTextOutline } from "react-icons/io5";
import { saveAs } from "file-saver";
import { AiOutlineDownload } from "react-icons/ai";

const RequestView = ({ setRequestView, currentItem }) => {
  const { t } = useTranslation();

  const downloadFile = () => {
    saveAs(currentItem?.cvFile, "cvFile.pdf");
  };

  const handleCopy = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-4xl">
          <div className="sm:py-4 sm:px-2 py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[940px] max-h-[85vh] overflow-y-auto">
              
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t sticky top-0 bg-white z-10">
                <h3 className="text-xl font-semibold">{t("O_VIEW_USER")}</h3>
                <button
                  className="ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none hover:bg-gray-100"
                  onClick={() => setRequestView(false)}
                >
                  <span className="text-[#B8BBBF] text-4xl" title="Close">
                    ×
                  </span>
                </button>
              </div>

              <div className="relative p-6 flex-auto space-y-6">
                
                {/* --- INTERVIEW STATUS BANNER --- */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Interview Status</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                      ${currentItem?.interviewStatus === 'scheduled' ? 'bg-green-100 text-green-700' : 
                        currentItem?.interviewStatus === 'completed' ? 'bg-blue-100 text-blue-700' : 
                        'bg-gray-200 text-gray-700'}`}>
                      {currentItem?.interviewStatus || "Pending"}
                    </span>
                  </div>

                  {currentItem?.interviewDate && (
                    <div className="bg-white p-4 rounded-lg border border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Scheduled Details</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        
                        <div>
                          <span className="text-gray-500 block mb-1 text-xs">Date & Time</span>
                          <span className="font-medium text-gray-900">
                            {dayjs(currentItem.interviewDate).format("MM-DD-YYYY hh:mm A")}
                          </span>
                        </div>

                        <div>
                          <span className="text-gray-500 block mb-1 text-xs">Type</span>
                          <span className="font-medium text-gray-900 capitalize">
                            {currentItem.interviewType || "N/A"}
                          </span>
                        </div>

                        {/* Zoom Specifics */}
                        {currentItem?.interviewType === "zoom" && currentItem?.interviewMeetingData && (
                          <>
                            <div>
                              <span className="text-gray-500 block mb-1 text-xs">Meeting ID</span>
                              <span className="font-medium text-gray-900">{currentItem.interviewMeetingData.id || "N/A"}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block mb-1 text-xs">Password</span>
                              <span className="font-medium text-gray-900">{currentItem.interviewMeetingData.password || "N/A"}</span>
                            </div>
                            <div className="col-span-2 md:col-span-4 mt-2">
                              <span className="text-gray-500 block mb-1 text-xs">Start URL</span>
                              <div className="flex items-center gap-2 bg-gray-50 p-2 rounded border">
                                <a
                                  href={currentItem.interviewMeetingData.start_url || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 text-xs truncate hover:underline flex-1"
                                >
                                  {currentItem.interviewMeetingData.start_url || "N/A"}
                                </a>
                                {currentItem.interviewMeetingData.start_url && (
                                  <button
                                    onClick={() => handleCopy(currentItem.interviewMeetingData.start_url)}
                                    className="text-xs bg-white border border-gray-300 px-3 py-1 rounded hover:bg-gray-100 text-gray-700 shadow-sm whitespace-nowrap"
                                  >
                                    Copy Link
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        {/* AI Specifics */}
                        {currentItem?.interviewType === "ai" && (
                          <div className="col-span-2 md:col-span-4">
                            <span className="text-gray-500 block mb-1 text-xs">AI Session ID</span>
                            <span className="font-medium text-gray-900">{currentItem.aiInterviewSessionID || "N/A"}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <hr className="border-gray-200" />

                {/* --- EMPLOYEE DETAILS --- */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Employee Details</h3>
                  <div className="grid grid-cols-3 gap-y-5 gap-x-6">
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Employee Name</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Employee?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Email ID</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Employee?.email || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Year of Experience</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.yearOfExperiece || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">DOB</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Employee?.dob ? dayjs(currentItem.Employee.dob).format("MM-DD-YYYY") : "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Country</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.EmployeeCountry?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">State</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.EmployeeState?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">City</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Employee?.city || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Skills</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.EmployeeSkills?.map((skill) => skill?.name).join(", ") || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Job Applied Date</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.createdAt ? dayjs(currentItem.createdAt).format("MM-DD-YYYY hh:mm A") : "-"}</h6>
                    </div>
                    <div className="col-span-3">
                      <strong className="block text-xs text-gray-500 mb-1">LinkedIn</strong>
                      <h6 className="text-sm font-medium text-blue-600">
                        {currentItem?.Employee?.linkedInLink ? (
                          <a href={currentItem.Employee.linkedInLink} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            View Profile
                          </a>
                        ) : (
                          <span className="text-gray-500 italic">LinkedIn profile not added</span>
                        )}
                      </h6>
                    </div>
                  </div>
                </div>

                <hr className="border-gray-200" />

                {/* --- JOB DETAILS --- */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Details</h3>
                  <div className="grid grid-cols-3 gap-y-5 gap-x-6 mb-6">
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Job Title</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Job?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Category</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.JobCategory?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Subcategory</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.JobSubCategory?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Department</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.JobDepartment?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Country</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.JobCountry?.name || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">City</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Job?.city || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Skills</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.JobSkills?.map((jobSkill) => jobSkill?.name).join(", ") || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Employment Length (Days)</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Job?.employmentLength || "-"}</h6>
                    </div>
                    <div>
                      <strong className="block text-xs text-gray-500 mb-1">Created Date</strong>
                      <h6 className="text-sm font-medium text-gray-900">{currentItem?.Job?.createdAt ? dayjs(currentItem.Job.createdAt).format("MM-DD-YYYY hh:mm A") : "-"}</h6>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <strong className="block text-sm font-semibold text-gray-800 mb-2">Job Description</strong>
                      <div className="inner-html text-sm text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-100 max-h-60 overflow-y-auto" dangerouslySetInnerHTML={{ __html: currentItem?.Job?.description }} />
                    </div>
                    <div>
                      <strong className="block text-sm font-semibold text-gray-800 mb-2">Termination Description</strong>
                      <div className="inner-html text-sm text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-100 max-h-60 overflow-y-auto" dangerouslySetInnerHTML={{ __html: currentItem?.Job?.terminationCondition }} />
                    </div>
                  </div>
                  
                  {/* Resume Section */}
                  <div className="mt-6">
                    <strong className="block text-sm font-semibold text-gray-800 mb-3">Resume / CV</strong>
                    <div className="w-64 border-2 border-gray-200 rounded-lg p-3 flex items-center justify-between hover:border-blue-300 transition-colors bg-white">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <IoDocumentTextOutline className="text-4xl text-blue-500 shrink-0" />
                        <span className="text-sm font-medium text-gray-700 truncate">Resume.pdf</span>
                      </div>
                      <button
                        type="button"
                        className="p-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition"
                        title="Download"
                        onClick={downloadFile}
                      >
                        <AiOutlineDownload size={20} />
                      </button>
                    </div>
                  </div>

                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end p-4 border-t border-solid border-slate-200 rounded-b bg-gray-50">
                <button
                  className="text-gray-700 bg-white border border-gray-300 font-medium px-8 py-2 text-sm outline-none focus:outline-none rounded hover:bg-gray-50 transition-all duration-150 shadow-sm"
                  type="button"
                  onClick={() => setRequestView(false)}
                >
                  {t("O_BACK")}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default RequestView;





// import React from "react";
// import { useTranslation } from "react-i18next";
// import dayjs from "dayjs";
// import { IoDocumentTextOutline } from "react-icons/io5";
// import { saveAs } from "file-saver";
// import { AiOutlineDownload } from "react-icons/ai";

// const RequestView = ({ setRequestView, currentItem }) => {
//   const { t } = useTranslation();
//   const downloadFile = () => {
//     saveAs(currentItem?.cvFile, "cvFile.pdf");
//   };

//   return (
//     <>
//       <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
//         <div className="relative w-auto my-6 mx-auto max-w-3xl">
//           <div className="sm:py-4 sm:px-2 py-8 px-7">
//             <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[940px] max-h-[85vh] overflow-y-auto	">
//               <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
//                 <h3 className="text-xl font-semibold">{t("O_VIEW_USER")}</h3>
//                 <button className=" ml-auto flex items-center justify-center  text-black border-2 rounded-full  h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none" onClick={() => setRequestView(false)}>
//                   <span className=" text-[#B8BBBF]  text-4xl " title="Close">
//                     ×
//                   </span>
//                 </button>
//               </div>
//               <div className="relative p-6 flex-auto">
//                 <h3 className="text-xl font-semibold">Employee details</h3>
//                 <div className="grid grid-cols-3">
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Employee name</strong>
//                       <h6>{currentItem?.Employee?.name || ""}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Employee email ID</strong>
//                       <h6>{currentItem?.Employee?.email || ""}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-6 w-full group">
//                       <strong>Year of experience</strong>
//                       <h6>{currentItem.yearOfExperiece || ""}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>DOB</strong>
//                       <h6>{dayjs(currentItem?.Employee?.dob).format("MM-DD-YYYY")}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Country</strong>
//                       <h6>{currentItem?.EmployeeCountry?.name} </h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>State</strong>
//                       <h6>{currentItem?.EmployeeState?.name} </h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>City</strong>
//                       <h6>{currentItem.Employee?.city || ""}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-6 w-full group">
//                       <strong>Skills</strong>
//                       <h6>{currentItem?.EmployeeSkills?.map((skill) => skill?.name).join(", ")}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Job applied date</strong>
//                       <h6>{dayjs(currentItem?.createdAt).format("MM-DD-YYYY hh:mm A")} </h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>LinkedIn</strong>
//                       <h6>{currentItem.Employee?.linkedInLink ? (
//                         <a href={currentItem.Employee.linkedInLink} target="_blank" rel="noopener noreferrer">
//                           View Profile
//                         </a>
//                       ) : (
//                         <span>LinkedIn profile not added</span>
//                       )}</h6>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//               <div className="px-6">
//                 {" "}
//                 <hr />
//               </div>
//               <div className="p-6 relative flex-auto">
//                 <h3 className="font-semibold text-xl ">Job details</h3>
//                 <div className="grid-cols-3 grid ">
//                   <div className="sm:py-4 py-8 px-7 sm:px-2 ">
//                     <div className="mb-2 w-full relative z-0 group">
//                       <strong>Job Title</strong>
//                       <h6>{currentItem?.Job?.name || ""}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Category</strong>
//                       <h6>{currentItem?.JobCategory?.name || ""}</h6>
//                     </div>
//                   </div>

//                   <div className="py-8 sm:py-4 sm:px-2 px-7">
//                     <div className="group relative z-0 mb-2 w-full ">
//                       <strong>Country</strong>
//                       <h6>{currentItem?.JobCountry?.name || ""}</h6>
//                     </div>
//                   </div>
//                   <div className="py-8 px-7 sm:py-4 sm:px-2 ">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>City</strong>
//                       <h6>{currentItem?.Job?.city || ""}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Subcategory</strong>
//                       <h6>{currentItem?.JobSubCategory?.name}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:px-2 py-8 sm:py-4 px-7">
//                     <div className="relative mb-2 w-full z-0 group">
//                       <strong>Department</strong>
//                       <h6>{currentItem?.JobDepartment?.name} </h6>
//                     </div>
//                   </div>

//                   <div className="px-7 sm:py-4 sm:px-2 py-8 ">
//                     <div className="z-0 mb-2 w-full group relative ">
//                       <strong>Skills</strong>
//                       <h6>{currentItem?.JobSkills?.map((jobSkill) => jobSkill?.name).join(", ")}</h6>
//                     </div>
//                   </div>
//                   <div className="py-8 sm:py-4 sm:px-2 px-7">
//                     <div className=" w-full relative z-0 mb-2 group">
//                       <strong>Employment length (In days)</strong>
//                       <h6>{currentItem?.Job?.employmentLength}</h6>
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Created date</strong>
//                       <h6>{dayjs(currentItem?.Job?.createdAt).format("MM-DD-YYYY hh:mm A")} </h6>
//                     </div>
//                   </div>
//                 </div>
//                 <div className="grid">
//                   <div className="sm:py-4 py-8 px-7 sm:px-2 ">
//                     <div className="relative w-full z-0 mb-2 group">
//                       <strong>Job description</strong>
//                       <div className="inner-html" dangerouslySetInnerHTML={{ __html: currentItem?.Job?.description }} />
//                     </div>
//                   </div>
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Termination description</strong>
//                       <div className="inner-html" dangerouslySetInnerHTML={{ __html: currentItem?.Job?.terminationCondition }} />
//                     </div>
//                   </div>
//                   <div className="grid grid-cols-1">
//                     <div className="sm:pt-4 sm:px-2 pt-8 pb-0 px-7">
//                       <div className="relative z-0 mb-0 w-full group">
//                         <strong className="mb-3 block">Resume</strong>
//                         <div className="grid-cols-5 grid gap-x-4">
//                           <div className="border-2 flex items-center rounded-lg p-3 justify-between">
//                             <div className="overflow-hidden item object-cover ">
//                               <span>
//                                 <IoDocumentTextOutline className="text-4xl" />
//                               </span>
//                             </div>
//                             <button type="button" className="mb-2 py-2 bg-LightBlue text-sm px-2 mr-3 mt-2 ml-3 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue" title="Download" onClick={downloadFile}>
//                               <AiOutlineDownload
//                                 style={{
//                                   fontWeight: 800,
//                                   width: "15px",
//                                 }}
//                               />
//                             </button>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                   {/* CHANGED — Zoom join/start link removed. AI interview status,
//                       transcript, and scores now live in the Report modal
//                       (interview/ReportView.jsx), opened from the Action column
//                       once interviewStatus === "completed". */}
//                   <div className="sm:py-4 sm:px-2 py-8 px-7">
//                     <div className="relative z-0 mb-2 w-full group">
//                       <strong>Interview Status</strong>
//                       <h6 className="capitalize">{currentItem?.interviewStatus || "Pending"}</h6>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
//                 <button className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150" type="button" onClick={() => setRequestView(false)}>
//                   {t("O_BACK")}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className="opacity-25 fixed inset-0 z-40 bg-black" />
//     </>
//   );
// };

// export default RequestView;